import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  fetchAdminPublicContractById,
  fetchAdminPublicContractCounts,
  fetchAdminPublicContracts,
  updateAdminPublicContractStatus,
} from "../../apis/publicContract";
import {
  AdminPublicContractListParams,
  PUBLIC_CONTRACT_STATUSES,
  PublicContract,
  PublicContractCounts,
  PublicContractListResult,
  PublicContractStatus,
} from "./types/publicContractTypes";
import {
  unwrapPaginatedPublicContracts,
  unwrapResponseData,
} from "./publicContractParse";

interface PublicContractState {
  list: PublicContractListResult;
  detail: PublicContract | null;
  counts: PublicContractCounts;
  listLoading: boolean;
  detailLoading: boolean;
  countsLoading: boolean;
  updating: boolean;
  error: string | null;
}

const initialState: PublicContractState = {
  list: {
    items: [],
    meta: {
      totalItems: 0,
      totalPages: 1,
      itemsPerPage: 10,
      currentPage: 1,
    },
  },
  detail: null,
  counts: {},
  listLoading: false,
  detailLoading: false,
  countsLoading: false,
  updating: false,
  error: null,
};

export const fetchPublicContractsAsync = createAsyncThunk(
  "publicContract/fetchList",
  async (params: AdminPublicContractListParams, { rejectWithValue }) => {
    try {
      const response = await fetchAdminPublicContracts(params);
      return unwrapPaginatedPublicContracts(response);
    } catch (error: any) {
      const message =
        error.response?.data?.message ?? "Failed to fetch public contracts";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const fetchPublicContractByIdAsync = createAsyncThunk(
  "publicContract/fetchDetail",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetchAdminPublicContractById(id);
      const data = unwrapResponseData(response) as any;
      return (data?.id
        ? data
        : data?.contract ?? data?.publicContract ?? data) as PublicContract;
    } catch (error: any) {
      const message =
        error.response?.data?.message ?? "Failed to fetch public contract";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const fetchPublicContractCountsAsync = createAsyncThunk(
  "publicContract/fetchCounts",
  async (_, { rejectWithValue }) => {
    try {
      const responses = await fetchAdminPublicContractCounts();
      const counts: PublicContractCounts = {};
      const keys = ["ALL", ...PUBLIC_CONTRACT_STATUSES];
      responses.forEach((payload, index) => {
        const { meta } = unwrapPaginatedPublicContracts(payload);
        counts[keys[index]] = meta.totalItems;
      });
      return counts;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch contract counts"
      );
    }
  }
);

export const updatePublicContractStatusAsync = createAsyncThunk(
  "publicContract/updateStatus",
  async (
    payload: { id: string; status: PublicContractStatus },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateAdminPublicContractStatus(
        payload.id,
        payload.status
      );
      toast.success(
        response?.message ?? "Public contract status updated"
      );
      return unwrapResponseData(response) as PublicContract;
    } catch (error: any) {
      const message =
        error.response?.data?.message ?? "Failed to update contract status";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const publicContractSlice = createSlice({
  name: "publicContracts",
  initialState,
  reducers: {
    resetPublicContractDetail: (state) => {
      state.detail = null;
      state.detailLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicContractsAsync.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(
        fetchPublicContractsAsync.fulfilled,
        (state, action: PayloadAction<PublicContractListResult>) => {
          state.listLoading = false;
          state.list = action.payload;
        }
      )
      .addCase(
        fetchPublicContractsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.listLoading = false;
          state.error = action.payload;
        }
      )
      .addCase(fetchPublicContractByIdAsync.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(
        fetchPublicContractByIdAsync.fulfilled,
        (state, action: PayloadAction<PublicContract>) => {
          state.detailLoading = false;
          state.detail = action.payload;
        }
      )
      .addCase(
        fetchPublicContractByIdAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.detailLoading = false;
          state.error = action.payload;
        }
      )
      .addCase(fetchPublicContractCountsAsync.pending, (state) => {
        state.countsLoading = true;
      })
      .addCase(
        fetchPublicContractCountsAsync.fulfilled,
        (state, action: PayloadAction<PublicContractCounts>) => {
          state.countsLoading = false;
          state.counts = action.payload;
        }
      )
      .addCase(fetchPublicContractCountsAsync.rejected, (state) => {
        state.countsLoading = false;
      })
      .addCase(updatePublicContractStatusAsync.pending, (state) => {
        state.updating = true;
      })
      .addCase(
        updatePublicContractStatusAsync.fulfilled,
        (state, action: PayloadAction<PublicContract>) => {
          state.updating = false;
          if (action.payload?.id) {
            state.detail = { ...state.detail, ...action.payload };
            state.list.items = state.list.items.map((item) =>
              item.id === action.payload.id
                ? { ...item, ...action.payload }
                : item
            );
          }
        }
      )
      .addCase(updatePublicContractStatusAsync.rejected, (state) => {
        state.updating = false;
      });
  },
});

export const { resetPublicContractDetail } = publicContractSlice.actions;
export default publicContractSlice.reducer;
